import React, { useCallback, useContext, useRef } from 'react'

import { ParallaxSlideProvider } from '../../../contexts/parallax-context'
import { TypeSectionTwoColumn } from '../../../lib/types'
import { ContentRenderer } from '../../renderer'
import { useFirstEnterViewport, useViewportIntersection } from '../../ui-lib/hooks/viewport-tracker'
import { PageContext } from '../../../contexts/page-context'
import { gnarSpec } from '../../../lib/tracking/gnar'

import styles from './parallax.module.scss'

interface ParallaxResponsiveProps {
  containerInternalName: string
  content: TypeSectionTwoColumn[]
  setActiveSlideIndex: (index: number) => void
}

export const ParallaxResponsive: React.FC<ParallaxResponsiveProps> = ({
  containerInternalName,
  content,
  setActiveSlideIndex,
}) => {
  return (
    <div className={styles.parallaxResponsiveContainer}>
      {content.map((row, idx) => {
        return (
          <ParallaxSlideProvider key={idx} value={{ containerName: containerInternalName, index: idx }}>
            <Slide slideIndex={idx} setActiveSlideIndex={setActiveSlideIndex} content={row} />
          </ParallaxSlideProvider>
        )
      })}
    </div>
  )
}

interface SlideProps {
  slideIndex: number
  setActiveSlideIndex: (index: number) => void
  content: TypeSectionTwoColumn
}

const Slide: React.FC<SlideProps> = ({ slideIndex, setActiveSlideIndex, content }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { pageId, slug, isSectionScrollTrackingEnabled } = useContext(PageContext)

  const onFirstEnter = useCallback(() => {
    if (isSectionScrollTrackingEnabled) {
      gnarSpec.sectionScroll(content.fields.internalName, pageId, slug)
    }
  }, [content.fields.internalName, isSectionScrollTrackingEnabled, pageId, slug])

  useFirstEnterViewport(ref, onFirstEnter, 0.5)

  useViewportIntersection(
    ref,
    () => {
      setActiveSlideIndex(slideIndex)
    },
    undefined,
    1
  )

  return (
    <div ref={ref} className={styles.slideContainer}>
      <ContentRenderer structure={content.fields.leftColumnContent} />
      <ContentRenderer structure={content.fields.rightColumnContent} />
    </div>
  )
}
